import { useEffect, useRef, useState } from 'react';
import type { TrackingKpiSnapshot } from './pickupTrackingTypes';

export interface TweenedKpi {
    appointments: number;
    pickedUp: number;
    drivers: number;
    failed: number;
}

// 与派送大屏 useKpiInterpolation 同算法（那边字段写死为派送指标，不动线上页面，这里复制一份）：
// 1s 一个 tick，总时长由 durationMs 决定，对齐 KPI slot 周期
const TICK_MS = 1000;
const ZERO: TweenedKpi = { appointments: 0, pickedUp: 0, drivers: 0, failed: 0 };

const toNumeric = (s: TrackingKpiSnapshot | null | undefined): TweenedKpi | null =>
    s
        ? {
              appointments: Number(s.appointments) || 0,
              pickedUp: Number(s.pickedUp) || 0,
              drivers: Number(s.drivers) || 0,
              failed: Number(s.failed) || 0,
          }
        : null;

// 本地已展示值兜底：previous 来自上一个 slot，而用户本地可能已经看过更晚的 current；
// 否则下一轮 previous 比已展示值还小，会出现「数字回退」。用 sessionStorage 存最后展示到的值，按字段取 max
const readBaseline = (key: string | null): TweenedKpi | null => {
    if (!key) return null;
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const p = JSON.parse(raw) as Partial<TweenedKpi> | null;
        if (!p) return null;
        return {
            appointments: Number(p.appointments) || 0,
            pickedUp: Number(p.pickedUp) || 0,
            drivers: Number(p.drivers) || 0,
            failed: Number(p.failed) || 0,
        };
    } catch {
        return null;
    }
};

const writeBaseline = (key: string | null, values: TweenedKpi): void => {
    if (!key) return;
    try {
        sessionStorage.setItem(key, JSON.stringify(values));
    } catch {
        // sessionStorage 满 / 隐私模式禁用等异常吞掉，不影响补间本身
    }
};

const maxAcrossFields = (a: TweenedKpi, b: TweenedKpi): TweenedKpi => ({
    appointments: Math.max(a.appointments, b.appointments),
    pickedUp: Math.max(a.pickedUp, b.pickedUp),
    drivers: Math.max(a.drivers, b.drivers),
    failed: Math.max(a.failed, b.failed),
});

const sameValues = (a: TweenedKpi, b: TweenedKpi): boolean =>
    a.appointments === b.appointments &&
    a.pickedUp === b.pickedUp &&
    a.drivers === b.drivers &&
    a.failed === b.failed;

/**
 * previous → current 的补间。
 * - calcSlot 前进或 storageKey 变化（切 hub）时启动新一轮
 * - 起点：previous 与本地基线按字段取 max；没有 previous 时取基线，再没有从 0 起（首屏 count-up）
 * - 终点被抬到不低于本地基线，避免反向补
 * - 匀速 linear，1s 一个 tick
 */
export const useCountTween = (
    current: TrackingKpiSnapshot | null | undefined,
    previous: TrackingKpiSnapshot | null | undefined,
    storageKey: string | null,
    durationMs: number,
): TweenedKpi | null => {
    const [values, setValues] = useState<TweenedKpi | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const lastSlotRef = useRef<string | null>(null);
    const lastKeyRef = useRef<string | null>(null);

    // 卸载时清 timer；不能放进主 effect 的 cleanup，否则 deps 变化会杀掉正在跑的 interval
    useEffect(
        () => () => {
            if (timerRef.current !== null) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        },
        [],
    );

    useEffect(() => {
        const clearTimer = () => {
            if (timerRef.current !== null) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };

        const rawTarget = toNumeric(current);
        if (!rawTarget) return;

        if (storageKey !== lastKeyRef.current) {
            lastKeyRef.current = storageKey;
            lastSlotRef.current = null;
        }

        const slot = current?.calcSlot ?? null;
        if (slot !== null && slot === lastSlotRef.current) return;
        lastSlotRef.current = slot;

        const rawStart = toNumeric(previous);
        const baseline = readBaseline(storageKey);
        const start = rawStart
            ? baseline
                ? maxAcrossFields(rawStart, baseline)
                : rawStart
            : baseline ?? ZERO;
        const target = baseline ? maxAcrossFields(rawTarget, baseline) : rawTarget;

        if (sameValues(start, target)) {
            clearTimer();
            setValues(target);
            writeBaseline(storageKey, target);
            return;
        }

        clearTimer();
        const startTime = performance.now();
        const step = () => {
            const progress = Math.min(1, (performance.now() - startTime) / durationMs);
            setValues({
                appointments: start.appointments + (target.appointments - start.appointments) * progress,
                pickedUp: start.pickedUp + (target.pickedUp - start.pickedUp) * progress,
                drivers: start.drivers + (target.drivers - start.drivers) * progress,
                failed: start.failed + (target.failed - start.failed) * progress,
            });
            if (progress >= 1) {
                clearTimer();
                writeBaseline(storageKey, target);
            }
        };
        step();
        timerRef.current = setInterval(step, TICK_MS);
    }, [current, previous, storageKey, durationMs]);

    return values;
};
